import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { DetailFields, DetailSection, EndpointHint, LoadFailure, Skeleton } from '../../../core/index.js'
import type { FeedbackAdminApi } from '../../adminApi.js'
import { formatWhen } from '../../components/FeedbackStatus.js'
import type { ChangeRequestView } from '../../types.js'
import { ChangeOutcome, ChangeStatusChip, ChangeTimeline, LinkedFeedback } from './ChangeParts.js'
import { actorLabel, changeTitle } from './changeText.js'
import { RequirementEditor } from './RequirementEditor.js'
import { RequirementView, VersionHistory } from './RequirementView.js'
import { RootActions } from './RootActions.js'
import { changeQueryKey } from './useChangeStep.js'

type AdminApi = FeedbackAdminApi

function Approval({ change }: { change: ChangeRequestView }) {
  const approval = change.approval
  if (!approval) {
    return null
  }
  return (
    <DetailSection title="Approval">
      <DetailFields
        fields={[
          { label: 'Version approved', value: String(approval.version) },
          { label: 'By', value: `${approval.byName ?? approval.by} · ${formatWhen(approval.at)}` },
          { label: 'Content hash', value: approval.contentHash, mono: true },
          { label: 'Base commit', value: approval.baseSha ?? 'Not given', mono: approval.baseSha !== null },
        ]}
      />
    </DetailSection>
  )
}

function ChangeBody({ api, change, isRoot, onReload }: { api: AdminApi; change: ChangeRequestView; isRoot: boolean; onReload: () => void }) {
  const feedbackQuery = useQuery({ queryKey: ['admin', 'feedback'], queryFn: api.listFeedback })
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
        <h2 className="text-base font-black text-ink">{changeTitle(change)}</h2>
        <ChangeStatusChip status={change.status} />
        <EndpointHint className="ml-auto">GET /admin/changes/{'{changeRequestId}'}</EndpointHint>
        <p className="w-full text-[11px] text-ink-mid">
          <span className="font-mono">{change.changeRequestId}</span> · opened by {actorLabel(change.createdBy)} {formatWhen(change.createdAt)} · updated {formatWhen(change.updatedAt)}
        </p>
      </div>

      <RootActions api={api} change={change} isRoot={isRoot} />
      <ChangeOutcome change={change} />
      {change.status === 'draft' ? <RequirementEditor key={change.version} api={api} change={change} onReload={onReload} /> : <RequirementView change={change} />}
      <Approval change={change} />
      <LinkedFeedback
        feedbackIds={change.feedbackIds}
        items={feedbackQuery.data}
        isError={feedbackQuery.isError}
        isFetching={feedbackQuery.isFetching}
        onRetry={() => void feedbackQuery.refetch()}
      />
      <VersionHistory change={change} />
      <ChangeTimeline change={change} />
    </div>
  )
}

/** One change in full: where it stands, what the root can do with it, the requirement, the feedback it answers and its history. */
export function ChangeDetail({ api, changeRequestId, isRoot, onBack }: { api: AdminApi; changeRequestId: string; isRoot: boolean; onBack: () => void }) {
  const changeQuery = useQuery({ queryKey: changeQueryKey(changeRequestId), queryFn: () => api.getChange(changeRequestId) })
  return (
    <section className="border border-marine/15 bg-white p-4">
      <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-[11px] font-bold text-marine hover:text-marine-dk">
        <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
        All changes
      </button>
      {changeQuery.isPending ? (
        <Skeleton lines={5} label="Loading the change" />
      ) : changeQuery.isError ? (
        <LoadFailure message="Could not load this change." onRetry={() => void changeQuery.refetch()} retrying={changeQuery.isFetching} />
      ) : (
        <ChangeBody api={api} change={changeQuery.data} isRoot={isRoot} onReload={() => void changeQuery.refetch()} />
      )}
    </section>
  )
}
